import React, { useState, useEffect } from 'react'
import { ChevronRight, ArrowRight } from 'lucide-react'
import { useParams } from 'react-router-dom'

// Import industry images
import FMCGImage from '../assets/images/expertise/FMCG.jpg'
import ITPeripheralsImage from '../assets/images/expertise/IT-Peripherals.jpg'
import CosmeticsImage from '../assets/images/expertise/Cosmetics.jpg'
import StationeryPackagingImage from '../assets/images/expertise/Stationery-Packaging.jpg'
import PharmaceuticalsImage from '../assets/images/expertise/Pharmaceuticals.jpg'
import TextileGarmentsImage from '../assets/images/expertise/Textile-Garments.jpg'
import ElectronicElectricalsImage from '../assets/images/expertise/Electronic-Electricals.jpg'
import EngineeringGoodsImage from '../assets/images/expertise/Engineering-Goods.jpg'
import AutomobileAncillaryImage from '../assets/images/expertise/Automobile-Ancillary.jpg'
import eCommerceImage from '../assets/images/expertise/eCommerce.jpg'

// Import industry icons
import eCommerceIcon from '../assets/images/expertise/eCommerce-icon.png'
import CosmeticsIcon from '../assets/images/expertise/Cosmetics-icon.png'
import StationeryPackagingIcon from '../assets/images/expertise/Stationery-Packaging-icon.png'
import PharmaceuticalsIcon from '../assets/images/expertise/Pharmaceuticals-icon.png'
import ITPeripheralsIcon from '../assets/images/expertise/IT-Peripherals-icon.png'
import TextileGarmentsIcon from '../assets/images/expertise/Textile-Garments-icon.png'
import FMCGIcon from '../assets/images/expertise/FMCG-icon.png'
import ElectronicElectricalsIcon from '../assets/images/expertise/Electronic-Electricals-icon.png'
import EngineeringGoodsIcon from '../assets/images/expertise/Engineering-Goods-icon.png'
import AutomobileAncillaryIcon from '../assets/images/expertise/Automobile-Ancillary-icon.png'

// INDUSTRY DATA
const industries = [
  {
    name: 'eCommerce',
    slug: 'ecommerce',
    image: eCommerceImage,
    icon: eCommerceIcon,
    link: '/industry-expertise/eCommerce',
    tagline: 'Powering the Future of Online Commerce',
    description:
      'S Infinite Express delivers reliable and technology-driven eCommerce logistics solutions designed to help online businesses grow faster. From order pickup and seamless fulfillment to last-mile delivery and real-time tracking, we ensure every shipment reaches its destination safely and on time.',
    benefits: [
      'Fast and reliable order pickup and delivery.',
      'Seamless eCommerce order fulfillment.',
      'Real-time shipment tracking and visibility.',
      'Efficient last-mile delivery solutions.',
      'Flexible logistics for growing online businesses.',
      'Improved customer experience through timely deliveries.'
    ],
    services: [
      'Order Fulfillment',
      'Last-Mile Delivery',
      'Reverse Logistics',
      'E-commerce Distribution'
    ]
  },
  {
    name: 'Cosmetics',
    slug: 'cosmetics',
    image: CosmeticsImage,
    icon: CosmeticsIcon,
    link: '/industry-expertise/Cosmetics',
    tagline: 'Delivering Beauty with Care and Precision',
    description:
      'S Infinite Express provides specialized logistics solutions for cosmetics, skincare, beauty, and personal care products. We understand the importance of careful handling, secure transportation, inventory management, and timely distribution to maintain product quality and customer satisfaction.',
    benefits: [
      'Safe and careful handling of beauty products.',
      'Reliable transportation and distribution.',
      'Efficient inventory and shipment management.',
      'Timely deliveries across multiple locations.',
      'Secure packaging and shipment handling.',
      'Scalable logistics solutions for beauty brands.'
    ],
    services: [
      'Cosmetics Distribution',
      'Warehousing Support',
      'Retail Distribution',
      'Order Fulfillment'
    ]
  },
  {
    name: 'Stationery & Packaging',
    slug: 'stationery-packaging',
    image: StationeryPackagingImage,
    icon: StationeryPackagingIcon,
    link: '/industry-expertise/Stationery-Packaging',
    tagline: 'Keeping Businesses Supplied and Moving',
    description:
      'S Infinite Express offers efficient logistics and distribution solutions for stationery, office supplies, printing materials, and packaging products. Our transportation network ensures dependable deliveries, optimized movement of goods, and consistent supply chain operations.',
    benefits: [
      'Reliable transportation of stationery products.',
      'Efficient distribution of packaging materials.',
      'Optimized shipment planning and movement.',
      'Timely deliveries to businesses and retailers.',
      'Careful handling of bulk consignments.',
      'Flexible logistics for changing business needs.'
    ],
    services: [
      'Bulk Transportation',
      'Retail Distribution',
      'B2B Logistics',
      'Supply Chain Management'
    ]
  },
  {
    name: 'Pharmaceuticals',
    slug: 'pharmaceuticals',
    image: PharmaceuticalsImage,
    icon: PharmaceuticalsIcon,
    link: '/industry-expertise/Pharmaceuticals',
    tagline: 'Reliable Logistics for Healthcare Supply Chains',
    description:
      'S Infinite Express supports pharmaceutical businesses with dependable logistics solutions focused on secure transportation, careful shipment handling, and timely distribution. We help healthcare businesses maintain efficient supply chains and ensure essential products reach their destinations reliably.',
    benefits: [
      'Secure pharmaceutical shipment handling.',
      'Reliable transportation and distribution.',
      'Timely deliveries to healthcare partners.',
      'Efficient shipment visibility and tracking.',
      'Careful handling throughout transportation.',
      'Dependable pharmaceutical supply chain support.'
    ],
    services: [
      'Pharmaceutical Distribution',
      'Healthcare Logistics',
      'B2B Transportation',
      'Supply Chain Support'
    ]
  },
  {
    name: 'IT & Peripherals',
    slug: 'it-peripherals',
    image: ITPeripheralsImage,
    icon: ITPeripheralsIcon,
    link: '/industry-expertise/IT-Peripherals',
    tagline: 'Connecting Technology Through Smarter Logistics',
    description:
      'S Infinite Express delivers smart logistics solutions for IT equipment, computers, networking devices, and electronic peripherals. We focus on secure handling, efficient transportation, shipment visibility, and dependable delivery to protect valuable technology products throughout their journey.',
    benefits: [
      'Secure transportation of IT equipment.',
      'Careful handling of sensitive technology products.',
      'Real-time shipment tracking and visibility.',
      'Reliable delivery to businesses and retailers.',
      'Efficient distribution across multiple locations.',
      'Flexible logistics for technology businesses.'
    ],
    services: [
      'IT Equipment Transportation',
      'Electronics Distribution',
      'B2B Logistics',
      'Reverse Logistics'
    ]
  },
  {
    name: 'Textile & Garments',
    slug: 'textile-garments',
    image: TextileGarmentsImage,
    icon: TextileGarmentsIcon,
    link: '/industry-expertise/Textile-Garments',
    tagline: 'Moving Fashion Forward with Confidence',
    description:
      'S Infinite Express provides streamlined logistics solutions for textile manufacturers, garment brands, fashion retailers, and apparel businesses. Our transportation and distribution services help businesses move products efficiently while maintaining shipment safety and delivery timelines.',
    benefits: [
      'Reliable transportation of garments and textiles.',
      'Efficient distribution to retail destinations.',
      'Careful handling of apparel shipments.',
      'Timely deliveries for seasonal collections.',
      'Optimized logistics and shipment planning.',
      'Scalable support for fashion businesses.'
    ],
    services: [
      'Garment Transportation',
      'Textile Distribution',
      'Retail Logistics',
      'B2B Transportation'
    ]
  },
  {
    name: 'FMCG',
    slug: 'fmcg',
    image: FMCGImage,
    icon: FMCGIcon,
    link: '/industry-expertise/FMCG',
    tagline: 'Accelerating Everyday Essentials',
    description:
      'S Infinite Express provides dependable FMCG logistics designed to support manufacturers, distributors, retailers, and consumer brands. Our transportation and distribution solutions help ensure faster product movement, efficient inventory replenishment, and consistent deliveries across supply chains.',
    benefits: [
      'Faster movement of FMCG products.',
      'Reliable distribution to retail networks.',
      'Efficient inventory replenishment.',
      'Consistent and timely deliveries.',
      'Optimized transportation operations.',
      'Scalable logistics for growing consumer brands.'
    ],
    services: [
      'FMCG Distribution',
      'Retail Supply Chain',
      'B2B Transportation',
      'Inventory Movement'
    ]
  },
  {
    name: 'Electronic & Electricals',
    slug: 'electronic-electricals',
    image: ElectronicElectricalsImage,
    icon: ElectronicElectricalsIcon,
    link: '/industry-expertise/Electronic-Electricals',
    tagline: 'Delivering Technology with Precision',
    description:
      'S Infinite Express provides specialized logistics solutions for electronic and electrical products, including appliances, components, equipment, and accessories. We prioritize secure shipment handling, dependable transportation, and efficient distribution to support manufacturers, dealers, and retailers.',
    benefits: [
      'Secure transportation of electronic products.',
      'Careful handling of valuable consignments.',
      'Reliable distribution across destinations.',
      'Efficient shipment tracking and monitoring.',
      'Timely deliveries to dealers and retailers.',
      'Flexible logistics for electronic businesses.'
    ],
    services: [
      'Electronics Transportation',
      'Electrical Goods Distribution',
      'Dealer Logistics',
      'B2B Supply Chain'
    ]
  },
  {
    name: 'Engineering Goods',
    slug: 'engineering-goods',
    image: EngineeringGoodsImage,
    icon: EngineeringGoodsIcon,
    link: '/industry-expertise/Engineering-Goods',
    tagline: 'Engineering Reliable Supply Chain Solutions',
    description:
      'S Infinite Express offers comprehensive logistics solutions for engineering goods, industrial equipment, machinery, and components. Our transportation capabilities support efficient freight movement, secure cargo handling, and dependable deliveries for businesses across industrial sectors.',
    benefits: [
      'Reliable transportation of engineering goods.',
      'Secure handling of industrial consignments.',
      'Efficient freight movement and distribution.',
      'Timely deliveries to industrial destinations.',
      'Customized transportation solutions.',
      'Dependable support for industrial supply chains.'
    ],
    services: [
      'Industrial Transportation',
      'Engineering Goods Distribution',
      'B2B Logistics',
      'Freight Management'
    ]
  },
  {
    name: 'Automobile Ancillary',
    slug: 'automobile-ancillary',
    image: AutomobileAncillaryImage,
    icon: AutomobileAncillaryIcon,
    link: '/industry-expertise/Automobile-Ancillary',
    tagline: 'Driving Automotive Supply Chains Forward',
    description:
      'The automobile ancillary industry demands precision, speed, and dependable logistics. S Infinite Express provides efficient transportation and distribution solutions for automotive components, spare parts, and ancillary products. We help automotive businesses manage changing production requirements, product upgrades, and evolving market demands through reliable supply chain support.',
    benefits: [
      'Reliable transportation of automotive components.',
      'Timely delivery to manufacturers and dealers.',
      'Efficient movement of spare parts.',
      'Secure handling of automotive consignments.',
      'Optimized transportation and distribution.',
      'Dependable logistics for uninterrupted operations.'
    ],
    services: [
      'Automotive Parts Distribution',
      'Spare Parts Logistics',
      'Industrial Transportation',
      'Automotive Supply Chain'
    ]
  }
]

const benefits = [
  'Fast, accurate, and reliable shipment deliveries.',
  'Strong nationwide connectivity with multi-modal logistics solutions.',
  'Reduced transit times through advanced technology and infrastructure.',
  'Optimized inventory management with timely deliveries.',
  'Reliable post-delivery support to improve customer and partner satisfaction.',
  'Flexible logistics solutions aligned with your evolving business requirements.'
]

const ExploreOurExpertise = () => {
  const { industry } = useParams()

  const formatIndustryName = name =>
    (name || '').replace(/-/g, ' ').toLowerCase()

  const [selectedIndustry, setSelectedIndustry] = useState(() => {
    return (
      industries.find(
        item => item.name.toLowerCase() === formatIndustryName(industry)
      ) || industries[0]
    )
  })

  useEffect(() => {
    const matchedIndustry = industries.find(
      item => item.name.toLowerCase() === formatIndustryName(industry)
    )

    if (matchedIndustry) {
      setSelectedIndustry(matchedIndustry)
    } else {
      setSelectedIndustry(industries[0])
    }
  }, [industry])

  const handleIndustryChange = item => {
    setSelectedIndustry(item)

    const slug = item.name.toLowerCase().replace(/&/g, '').replace(/\s+/g, '-')

    window.history.pushState({}, '', `/industry-expertise/${slug}`)
  }

  return (
    <main className='min-h-screen overflow-hidden bg-[#f8fafc]'>
      {/* INDUSTRY DETAILS */}
      <section className='mx-auto max-w-7xl px-4 py-10 sm:px-6 md:py-12 lg:px-8'>
        {/* SECTION HEADING */}
        <div className='mb-7 sm:mb-9'>
          <span className='text-xs font-semibold uppercase tracking-[3px] text-orange-500'>
            Our Expertise
          </span>

          <h2 className='mt-2 text-3xl font-light leading-tight text-[#101c3d] sm:text-4xl lg:text-5xl'>
            Industries We <span className='font-semibold'>Serve</span>
          </h2>

          <p className='mt-3 max-w-2xl text-sm leading-7 text-gray-500 sm:text-base'>
            Explore our industry-focused logistics capabilities and discover
            solutions built for your business.
          </p>
        </div>

        <div className='grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8'>
          {/* INDUSTRY SIDEBAR */}
          <aside className='lg:col-span-4'>
            <div className='overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm'>
              {/* SELECTED INDUSTRY IMAGE */}
              <div className='group relative h-48 overflow-hidden sm:h-56 lg:h-52'>
                <img
                  src={selectedIndustry.image}
                  alt={selectedIndustry.name}
                  className='h-full w-full object-cover transition-transform duration-700 group-hover:scale-105'
                />

                <div className='absolute inset-0 bg-gradient-to-t from-[#101c3d]/90 via-[#101c3d]/20 to-transparent' />

                <div className='absolute bottom-5 left-5 right-5 sm:bottom-6 sm:left-6'>
                  <span className='text-[10px] font-semibold uppercase tracking-[2px] text-orange-300 sm:text-xs'>
                    Currently Exploring
                  </span>

                  <h3 className='mt-1 text-xl font-semibold text-white sm:text-2xl'>
                    {selectedIndustry.name}
                  </h3>
                </div>
              </div>

              {/* INDUSTRY LIST */}
              <div className='p-4 sm:p-5 lg:p-6'>
                <div className='mb-4 flex items-center justify-between gap-2'>
                  <h3 className='text-xs font-bold uppercase tracking-[2px] text-[#101c3d] sm:text-sm'>
                    Explore Industries
                  </h3>

                  <span className='shrink-0 text-xs font-medium text-gray-400'>
                    {String(
                      industries.findIndex(
                        item => item.name === selectedIndustry.name
                      ) + 1
                    ).padStart(2, '0')}{' '}
                    / {String(industries.length).padStart(2, '0')}
                  </span>
                </div>

                {/* MOBILE HORIZONTAL SCROLL / DESKTOP VERTICAL LIST */}
                <div className='-mx-1 flex gap-2 overflow-x-auto px-1 pb-2 scrollbar-thin lg:mx-0 lg:flex-col lg:gap-1 lg:overflow-visible lg:px-0 lg:pb-0'>
                  {industries.map((item, index) => {
                    const isActive = selectedIndustry.name === item.name

                    return (
                      <button
                        key={item.name}
                        onClick={() => handleIndustryChange(item)}
                        aria-pressed={isActive}
                        className={`group flex min-w-max shrink-0 items-center justify-between gap-4 rounded-lg px-4 py-3 text-left transition-all duration-300 lg:w-full lg:min-w-0 lg:py-3.5 ${
                          isActive
                            ? 'bg-[#101c3d] text-white shadow-md'
                            : 'bg-gray-50 text-gray-600 hover:bg-orange-50 hover:text-orange-600 lg:bg-transparent'
                        }`}
                      >
                        <div className='flex items-center gap-3'>
                          <span
                            className={`text-xs ${
                              isActive ? 'text-orange-400' : 'text-gray-400'
                            }`}
                          >
                            {String(index + 1).padStart(2, '0')}
                          </span>

                          <span className='text-xs font-medium sm:text-sm'>
                            {item.name}
                          </span>
                        </div>

                        <ChevronRight
                          size={16}
                          className={`shrink-0 transition-transform ${
                            isActive
                              ? 'translate-x-1 text-orange-400'
                              : 'text-gray-300 group-hover:translate-x-1'
                          }`}
                        />
                      </button>
                    )
                  })}
                </div>
              </div>
            </div>
          </aside>

          {/* INDUSTRY CONTENT */}
          <div className='lg:col-span-8'>
            <div className='h-full overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm'>
              {/* FEATURE IMAGE */}
              <div className='group relative h-60 overflow-hidden sm:h-72 md:h-80 lg:h-[360px]'>
                <img
                  src={selectedIndustry.image}
                  alt={selectedIndustry.name}
                  className='h-full w-full object-cover transition-transform duration-700 group-hover:scale-105'
                />

                <div className='absolute inset-0 bg-gradient-to-t from-[#101c3d]/95 via-[#101c3d]/20 to-transparent' />

                <div className='absolute bottom-5 left-5 right-5 sm:bottom-8 sm:left-8 md:bottom-9 md:left-10'>
                  <div className='mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-white shadow-lg sm:h-14 sm:w-14'>
                    <img
                      src={selectedIndustry.icon}
                      alt={`${selectedIndustry.name} icon`}
                      className='h-8 w-8 object-contain sm:h-10 sm:w-10'
                    />
                  </div>

                  <span className='text-[10px] font-semibold uppercase tracking-[2px] text-orange-300 sm:text-xs sm:tracking-[3px]'>
                    Industry Solutions
                  </span>

                  <h2 className='mt-2 text-2xl font-semibold leading-tight text-white sm:text-3xl md:text-4xl'>
                    {selectedIndustry.name}
                  </h2>
                </div>
              </div>

              {/* INDUSTRY DESCRIPTION */}
              <div className='p-5 sm:p-6 md:p-7 lg:p-8'>
                <div className='mb-7 sm:mb-9'>
                  <p className='text-sm leading-7 text-gray-600 sm:text-base sm:leading-8'>
                    {selectedIndustry.description}
                  </p>
                </div>

                {/* BENEFITS */}
                <div className='mb-7 sm:mb-9'>
                  <h3 className='mb-4 text-lg font-semibold text-[#101c3d] sm:mb-5 sm:text-xl'>
                    Why Choose Our Solutions?
                  </h3>

                  <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4'>
                    {benefits.map((benefit, index) => (
                      <div
                        key={index}
                        className='flex items-start gap-3 rounded-xl bg-[#f8fafc] p-4 transition-all duration-300 hover:bg-orange-50'
                      >
                        <span className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-orange-100 text-orange-500'>
                          <span className='text-sm font-bold'>✓</span>
                        </span>

                        <p className='text-sm leading-6 text-gray-600'>
                          {benefit}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA */}
                <div className='flex flex-col items-start justify-between gap-5 rounded-xl bg-[#101c3d] p-5 sm:p-6 md:flex-row md:items-center md:p-8'>
                  <div>
                    <h3 className='text-lg font-semibold leading-snug text-white sm:text-xl'>
                      Looking for logistics solutions?
                    </h3>

                    <p className='mt-2 text-sm leading-6 text-gray-300'>
                      Let's build a smarter supply chain for your business.
                    </p>
                  </div>

                  <a
                    href='/contact-us/'
                    className='inline-flex w-full shrink-0 items-center justify-center gap-3 rounded-lg bg-orange-500 px-6 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:bg-orange-600 hover:shadow-lg sm:w-auto'
                  >
                    Make an Inquiry
                    <ArrowRight size={17} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BOTTOM BRAND SECTION */}
      <section className='border-t border-gray-100 bg-white py-10 sm:py-12'>
        <div className='mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 text-center sm:px-6 md:flex-row md:px-8 md:text-left'>
          <div>
            <h3 className='text-2xl font-light text-[#101c3d] sm:text-3xl'>
              Moving Businesses <span className='font-semibold'>Forward.</span>
            </h3>

            <p className='mt-2 text-sm text-gray-500'>
              Reliable logistics. Connected possibilities.
            </p>
          </div>
        </div>
      </section>
    </main>
  )
}

export default ExploreOurExpertise
